import { useMemo, useState, type RefObject } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface Faq {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined' && s !== 'none' && s !== 'n/a';
}

interface FaqItemProps {
  faq: Faq;
  isOpen: boolean;
  onToggle: () => void;
  panelId: string;
  buttonId: string;
}

function FaqItem({ faq, isOpen, onToggle, panelId, buttonId }: FaqItemProps) {
  return (
    <div className={`faq-item${isOpen ? ' faq-item--open' : ''}`}>
      <h3 className="faq-item-question">
        <button
          id={buttonId}
          type="button"
          className="faq-item-toggle"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
        >
          <span>{faq.question || ''}</span>
          <span className="faq-item-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-item-panel"
        hidden={!isOpen}
      >
        {hasValue(faq.answer) && (
          <div
            className="faq-item-answer"
            dangerouslySetInnerHTML={{ __html: faq.answer! }}
          />
        )}
      </div>
    </div>
  );
}

interface FaqCategoryGroup {
  key: string;
  label: string | null;
  items: Faq[];
}

export function FAQList() {
  const rawFaqs = useCollectionBinding<Faq>('faqlist.items', {
    collection: 'faqs',
  });

  const faqs = rawFaqs || [];
  const [openId, setOpenId] = useState<string | null>(null);

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const groups: FaqCategoryGroup[] = useMemo(() => {
    if (faqs.length === 0) return [];

    // Bucket by category — falsy/blank categories go into a single
    // "uncategorized" bucket that renders without a heading.
    const map = new Map<string, FaqCategoryGroup>();
    for (const faq of faqs) {
      const raw = hasValue(faq.category) ? faq.category!.trim() : '';
      const key = raw.toLowerCase() || '__uncategorized__';
      const existing = map.get(key);
      if (existing) {
        existing.items.push(faq);
      } else {
        map.set(key, {
          key,
          label: raw || null,
          items: [faq],
        });
      }
    }

    return Array.from(map.values());
  }, [faqs]);

  const anyCategoryLabeled = groups.some((g) => g.label !== null);

  const hasContent = faqs.length > 0;

  return (
    <section ref={sectionRef as RefObject<HTMLElement>} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="faq-groups">
            {groups.map((group, groupIndex) => (
              <div key={group.key} className="faq-group">
                {anyCategoryLabeled && group.label && (
                  <h2 className="faq-group-heading">{group.label}</h2>
                )}
                <div className="faq-list">
                  {group.items.map((faq, itemIndex) => {
                    const itemId = `${group.key}-${itemIndex}`;
                    return (
                      <FaqItem
                        key={itemId}
                        faq={faq}
                        isOpen={openId === itemId}
                        onToggle={() => setOpenId(openId === itemId ? null : itemId)}
                        panelId={`faq-panel-${groupIndex}-${itemIndex}`}
                        buttonId={`faq-button-${groupIndex}-${itemIndex}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Frequently asked questions will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 400;
          color: var(--vibe-color-text);
          text-align: center;
          margin: 0 0 1.25rem;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid var(--vibe-color-border);
        }

        .faq-item {
          border-bottom: 1px solid var(--vibe-color-border);
          background: white;
        }

        .faq-item-question {
          margin: 0;
        }

        .faq-item-toggle {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1.1rem 1.25rem;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: left;
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          color: var(--vibe-color-text);
          letter-spacing: 0.01em;
          transition: color 0.2s, background 0.2s;
        }
        .faq-item-toggle:hover {
          color: var(--vibe-color-primary);
          background: rgba(0, 0, 0, 0.015);
        }
        .faq-item-toggle:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .faq-item-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          color: var(--vibe-color-primary);
          transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .faq-item--open .faq-item-icon {
          transform: rotate(180deg);
        }

        .faq-item-panel {
          padding: 0 1.25rem 1.25rem;
        }

        .faq-item-answer {
          font-family: var(--vibe-font-body);
          font-size: 0.925rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
        }
        .faq-item-answer p { margin: 0 0 0.75rem; }
        .faq-item-answer p:last-child { margin-bottom: 0; }
        .faq-item-answer ul,
        .faq-item-answer ol {
          padding-left: 1.25rem;
          margin: 0 0 0.75rem;
        }
        .faq-item-answer li { margin-bottom: 0.35rem; }
        .faq-item-answer a {
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        .faq-item-answer a:hover {
          opacity: 0.8;
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-item-icon {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {},
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQs',
      collection: 'faqs',
    },
  },
};

(FAQList as any).contentSchema = contentSchema;

export default FAQList;
